import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Card, PasswordText, Icon, BlockedAccountModal } from '@components';
import { Button, ButtonsContainer } from '@components/Forms';
import EmailSentModal from '@components/Modal/EmailSentModal';
import { useAuth, useForm } from '@hooks';
import { useSelector } from 'react-redux';

const Title = styled.h3(
  ({ theme }) => css`
    margin-top: 0;
    margin-bottom: 26px;
    ${theme.textTypes['H6-w400']};
    color: ${theme.colors.gray800};
    text-align: center;
    padding-left: 8px;
    padding-right: 8px;
  `
);

const EmailContainer = styled.div(
  ({ theme }) => css`
    border: 1px solid ${theme.colors.gray400};
    box-sizing: border-box;
    border-radius: 20px;
    padding: 10px 18px;
    display: inline-flex;
    align-items: center;
    margin-bottom: 18px;
    & svg {
      width: 20px !important;
      height: 20px !important;
    }
  `
);

const StyledPasswordText = styled(PasswordText)(
  ({ theme }) => css`
    & .MuiInputBase-root {
      padding: 14px 16px;
    }

    & .MuiFormHelperText-root.Mui-error {
      color: ${theme.colors.error};
    }
  `
);

const StyledEmail = styled.p(
  ({ theme }) => css`
    ${theme.textTypes['Body-2-w400']}
    color: ${theme.colors.gray800};
    margin: 0 auto;
    display: inline;
    margin-left: 8px;
  `
);

const FormPasswordConfirmation = ({ email, handleCancel, handleSubmit }) => {
  const { t } = useTranslation();
  const { formValidationErrors } = useSelector((state) => state.common);
  const [state, setState] = useState({
    relai: 0,
    error: false,
    showModalBlocked: false,
    showModalEmailSent: false,
  });
  const { doLogout } = useAuth();
  const { formValues, handleInputChange, isValid, errors, isInitial } = useForm({
    entryPassword: '',
  });

  const customHandleInputChange = (e) => {
    setState((prevState) => ({ ...prevState, error: false }));
    handleInputChange(e);
  };

  const handleLogout = () => {
    doLogout();
  };

  const handleToggleModalEmailSent = () => {
    setState((prevState) => ({ ...prevState, showModalEmailSent: !prevState.showModalEmailSent }));
  };

  useEffect(() => {
    if (isInitial()) return;
    if (formValidationErrors.hasError) {
      setState((prevState) => ({ ...prevState, error: true }));
      if (state.relai === 2) {
        setState((prevState) => ({ ...prevState, relai: 0, showModalBlocked: true }));
        return;
      }
      setState((prevState) => ({ ...prevState, relai: prevState.relai + 1 }));
    } else {
      setState((prevState) => ({ ...prevState, error: false, showModalEmailSent: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValidationErrors]);

  const handleConfirmation = () => {
    if (!isValid()) return '';
    handleToggleModalEmailSent();
    handleSubmit(formValues.entryPassword);
  };

  const hasError = () => !!errors.entryPassword || state.error;

  return (
    <>
      <Card>
        <Title>{t('commerceData.editRepresentant.passwordConfirmation.title')}</Title>
        <div>
          <EmailContainer>
            <Icon icon="user" size="24" color="#838aa2" />
            <StyledEmail>{email}</StyledEmail>
          </EmailContainer>
        </div>
        <>
          <StyledPasswordText
            id="entryPassword"
            name="entryPassword"
            variant="outlined"
            label={t('common.password')}
            gridProps={{ item: true, xs: 12, padding: '0' }}
            fullWidth
            value={formValues.entryPassword}
            onChange={customHandleInputChange}
            error={hasError()}
            helperText={hasError() && t('errors.password.incorrect')}
            autoComplete="off"
          />
          <ButtonsContainer>
            <Button
              style={{ maxWidth: '102px', marginRight: '16px' }}
              testId="formpasswordconfirmation-cancel-button"
              type="outlined"
              onClick={handleCancel}
            >
              {t('common.buttonCancel')}
            </Button>
            <Button
              style={{ maxWidth: '102px' }}
              testId="formpasswordconfirmation-continue-button"
              onClick={handleToggleModalEmailSent}
              disabled={isInitial() || hasError()}
            >
              {t('common.buttonNext')}
            </Button>
          </ButtonsContainer>
        </>
      </Card>
      {state.showModalBlocked && (
        <BlockedAccountModal
          fClose={handleLogout}
          title={t('commerceData.editRepresentant.forgotPassword.title')}
          subTitle={t('login.resetPassword.common.restorePassword')}
          textClose={t('login.resetPassword.common.title')}
        />
      )}
      {state.showModalEmailSent && (
        <EmailSentModal
          email={email}
          handleConfirm={handleConfirmation}
          handleClose={handleToggleModalEmailSent}
        />
      )}
    </>
  );
};

FormPasswordConfirmation.propsTypes = {
  email: PropTypes.string.isRequired,
  handleCancel: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
};

export default FormPasswordConfirmation;
